// File: /pages/ShuffleMaps/Events.jsx
"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import styles from "./Events.module.css";
import ListingCard from "/components/Shufflemaps/Listings/ListingCard";

const PAGE_SIZE = 24;

export default function EventsPage() {
  const [allListings, setAllListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState(null);

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest"); // 'newest' | 'rating' | 'popular'
  const [page, setPage] = useState(1);

  const topRef = useRef(null);

  // Fetch all listings once from existing endpoint
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch("/api/shufflemaps/listings/get-listings", {
          credentials: "include",
          cache: "no-store",
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = await r.json();
        if (!alive) return;
        setAllListings(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!alive) return;
        setErrorMsg(e?.message || "Failed to load listings");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Only "Events" category
  const eventListings = useMemo(() => {
    return (allListings || []).filter((l) =>
      Array.isArray(l.categories) ? l.categories.includes("Events") : false
    );
  }, [allListings]);

  // Text search across common fields
  const searched = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return eventListings;
    return eventListings.filter((l) => {
      const fields = [
        l.name,
        l.short_description,
        l.description,
        l.address,
        l.city,
        l.admin1_code,
        l.country_code,
        Array.isArray(l.categories) ? l.categories.join(" ") : "",
        Array.isArray(l.tags) ? l.tags.map((t) => t?.name || t).join(" ") : "",
      ];
      return fields.some((f) => String(f || "").toLowerCase().includes(q));
    });
  }, [eventListings, query]);

  // Sorting
  const sorted = useMemo(() => {
    const list = [...searched];
    if (sort === "rating") {
      list.sort((a, b) => {
        const ar = Number(a.rating ?? -1);
        const br = Number(b.rating ?? -1);
        if (ar !== br) return br - ar;
        const ac = Number(a.review_count ?? -1);
        const bc = Number(b.review_count ?? -1);
        return bc - ac;
      });
    } else if (sort === "popular") {
      list.sort((a, b) => {
        const ac = Number(a.review_count ?? -1);
        const bc = Number(b.review_count ?? -1);
        if (ac !== bc) return bc - ac;
        const ar = Number(a.rating ?? -1);
        const br = Number(b.rating ?? -1);
        return br - ar;
      });
    } else {
      list.sort((a, b) => {
        const ad = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const bd = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return bd - ad;
      });
    }
    return list;
  }, [searched, sort]);

  // Pagination
  const total = sorted.length;
  const visible = useMemo(() => sorted.slice(0, page * PAGE_SIZE), [sorted, page]);
  const hasMore = visible.length < total;

  // Handlers
  const submitSearch = (e) => {
    e.preventDefault();
    setPage(1);
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  const changeSort = (e) => {
    setSort(e.target.value);
    setPage(1);
    requestAnimationFrame(() =>
      topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
    );
  };

  return (
    <main className={styles.smMain}>
      <section ref={topRef} className={styles.smSection}>
        <h1 className={styles.smTitle}>Events</h1>
        <p className={styles.smSub}>
          Festivals, battles, parties, and public gatherings focused on shuffle dance.
        </p>

        <form className={styles.toolbar} onSubmit={submitSearch}>
          <input
            className={styles.input}
            placeholder="Search events (city, organizer)…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className={styles.select} value={sort} onChange={changeSort}>
            <option value="newest">Newest</option>
            <option value="rating">Highest Rated</option>
            <option value="popular">Most Popular</option>
          </select>
          <button className={styles.btn} type="submit">
            Search
          </button>
        </form>

        {loading ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>Loading…</div>
            <div className={styles.emptySub}>Fetching event listings.</div>
          </div>
        ) : errorMsg ? (
          <div className={styles.errorBox}>{errorMsg}</div>
        ) : total === 0 ? (
          <div className={styles.empty}>
            <div className={styles.emptyTitle}>No events found</div>
            <div className={styles.emptySub}>
              Try a different search term or clear your filters.
            </div>
          </div>
        ) : (
          <>
            <div className={styles.resultsHeader}>
              <div className={styles.resultsTitle}>
                {visible.length} of {total} events
              </div>
              <div className={styles.resultsMeta}>
                {query ? <>Filtered by “{query}”</> : <>Showing all</>}
              </div>
            </div>

            <div className={styles.cardsGrid}>
              {visible.map((l) => (
                <Link
                  key={l.id}
                  href={{ pathname: "/ShuffleMaps", query: { listingId: String(l.id) } }}
                  className={styles.cardWrap}
                  title="Open in ShuffleMaps"
                >
                  <ListingCard
                    listingId={l.id}
                    name={l.name}
                    status={l.status}
                    shortDescription={l.short_description ?? l.shortDescription}
                    suppressEmptyRatingDisplay
                  />
                </Link>
              ))}
            </div>

            {hasMore && (
              <div className={styles.loadMoreRow}>
                <button
                  className={styles.loadMoreBtn}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Load more
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
